import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Star, Mail } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import CookieBanner from '../components/CookieBanner';
import PricingTable from '../components/PricingTable';
import { useProducts } from '../hooks/useProducts';
import Button from '../components/ui/Button';

// ── Real Zodiac Constellation Data ──────────────────────────────────────────
// Stars are defined in a normalised 0-1 coordinate space; they get scaled to
// the target spread at render time.  `mag` (magnitude) controls relative
// brightness — lower = brighter, matching astronomical convention.
// Lines reference star indices.

// Real Aries constellation — compact diagonal stroke with a short descending tail,
// matching the classic Aries silhouette from the reference.
// Hamal (alpha) is the brightest and largest.
const ARIES_CONSTELLATION = {
  name: 'Aries',
  stars: [
    { x: 0.18, y: 0.24, mag: 2.7 },  // Sheratan (beta) — upper-left anchor
    { x: 0.63, y: 0.47, mag: 2.0 },  // Hamal (alpha) — brightest central star
    { x: 0.82, y: 0.61, mag: 3.4 },  // Mesarthim (gamma) — right branch
    { x: 0.86, y: 0.80, mag: 3.9 },  // 41 Arietis — short tail downward
  ],
  lines: [[0, 1], [1, 2], [2, 3]],   // Diagonal body with a short lower tail
};

// Real Taurus constellation — upper-left diagonal feeding a dense center,
// with a long left arm, a short right tail, and a small lower branch.
// Aldebaran (alpha) stays as the brightest star near the middle cluster.
const TAURUS_CONSTELLATION = {
  name: 'Taurus',
  stars: [
    { x: 0.22, y: 0.12, mag: 2.8 },  // Upper-left tip
    { x: 0.39, y: 0.28, mag: 3.0 },  // Diagonal mid
    { x: 0.48, y: 0.39, mag: 3.2 },  // Cluster upper-left
    { x: 0.56, y: 0.45, mag: 0.9 },  // Aldebaran (alpha) — brightest center
    { x: 0.62, y: 0.52, mag: 3.3 },  // Cluster lower-right
    { x: 0.49, y: 0.46, mag: 3.4 },  // Left side of cluster
    { x: 0.10, y: 0.30, mag: 3.2 },  // Long left arm anchor
    { x: 0.79, y: 0.58, mag: 3.4 },  // Right arm
    { x: 0.88, y: 0.58, mag: 3.2 },  // Right tip
    { x: 0.92, y: 0.72, mag: 3.8 },  // Short downward tail
    { x: 0.64, y: 0.82, mag: 3.6 },  // Lower branch tip
  ],
  lines: [[0, 1], [1, 2], [2, 3], [5, 3], [6, 5], [3, 4], [4, 7], [7, 8], [8, 9], [4, 10]],
  // Dense central knot with left arm, right tail, and a short lower branch.
};

const GEMINI_CONSTELLATION = {
  name: 'Gemini',
  stars: [
    { x: 0.18, y: 0.18, mag: 2.9 },
    { x: 0.30, y: 0.10, mag: 3.0 },
    { x: 0.38, y: 0.24, mag: 3.2 },
    { x: 0.26, y: 0.34, mag: 3.3 },
    { x: 0.44, y: 0.32, mag: 2.1 },
    { x: 0.58, y: 0.16, mag: 3.2 },
    { x: 0.62, y: 0.42, mag: 3.4 },
    { x: 0.76, y: 0.54, mag: 3.1 },
    { x: 0.86, y: 0.52, mag: 3.3 },
    { x: 0.58, y: 0.70, mag: 3.4 },
    { x: 0.38, y: 0.62, mag: 3.2 },
    { x: 0.48, y: 0.84, mag: 3.5 },
  ],
  lines: [[0, 1], [0, 2], [0, 3], [2, 4], [4, 5], [4, 6], [6, 7], [7, 8], [4, 9], [9, 10], [10, 11]],
};

const CANCER_CONSTELLATION = {
  name: 'Cancer',
  stars: [
    { x: 0.52, y: 0.10, mag: 3.0 },
    { x: 0.54, y: 0.34, mag: 3.2 },
    { x: 0.52, y: 0.56, mag: 2.3 },
    { x: 0.34, y: 0.80, mag: 3.4 },
    { x: 0.76, y: 0.78, mag: 3.4 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [2, 4]],
};

const LEO_CONSTELLATION = {
  name: 'Leo',
  stars: [
    { x: 0.12, y: 0.72, mag: 3.3 },
    { x: 0.28, y: 0.48, mag: 3.0 },
    { x: 0.50, y: 0.42, mag: 3.2 },
    { x: 0.74, y: 0.38, mag: 2.2 },
    { x: 0.74, y: 0.64, mag: 3.0 },
    { x: 0.56, y: 0.62, mag: 3.4 },
    { x: 0.86, y: 0.22, mag: 3.2 },
    { x: 0.96, y: 0.30, mag: 3.3 },
    { x: 0.86, y: 0.50, mag: 3.3 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [5, 4], [3, 6], [6, 7], [3, 8]],
};

const VIRGO_CONSTELLATION = {
  name: 'Virgo',
  stars: [
    { x: 0.12, y: 0.58, mag: 3.3 },
    { x: 0.34, y: 0.50, mag: 3.1 },
    { x: 0.48, y: 0.26, mag: 3.0 },
    { x: 0.66, y: 0.40, mag: 2.4 },
    { x: 0.88, y: 0.18, mag: 3.2 },
    { x: 0.56, y: 0.68, mag: 3.0 },
    { x: 0.40, y: 0.82, mag: 3.4 },
    { x: 0.18, y: 0.80, mag: 3.5 },
    { x: 0.72, y: 0.58, mag: 3.1 },
    { x: 0.84, y: 0.42, mag: 3.3 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [3, 8], [8, 9], [3, 5], [5, 6], [6, 7], [1, 5]],
};

const LIBRA_CONSTELLATION = {
  name: 'Libra',
  stars: [
    { x: 0.22, y: 0.46, mag: 3.2 },
    { x: 0.42, y: 0.34, mag: 3.0 },
    { x: 0.52, y: 0.12, mag: 2.4 },
    { x: 0.78, y: 0.34, mag: 3.1 },
    { x: 0.68, y: 0.68, mag: 2.9 },
    { x: 0.40, y: 0.80, mag: 3.4 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5]],
};

const SCORPIO_CONSTELLATION = {
  name: 'Scorpio',
  stars: [
    { x: 0.14, y: 0.62, mag: 3.2 },
    { x: 0.22, y: 0.50, mag: 3.1 },
    { x: 0.20, y: 0.40, mag: 3.3 },
    { x: 0.12, y: 0.30, mag: 3.4 },
    { x: 0.24, y: 0.24, mag: 3.1 },
    { x: 0.42, y: 0.28, mag: 2.8 },
    { x: 0.56, y: 0.42, mag: 3.0 },
    { x: 0.60, y: 0.60, mag: 3.2 },
    { x: 0.70, y: 0.72, mag: 3.0 },
    { x: 0.82, y: 0.68, mag: 3.1 },
    { x: 0.90, y: 0.56, mag: 3.2 },
    { x: 0.94, y: 0.40, mag: 3.0 },
    { x: 0.88, y: 0.24, mag: 3.2 },
    { x: 0.82, y: 0.18, mag: 3.3 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13]],
};

const SAGITTARIUS_CONSTELLATION = {
  name: 'Sagittarius',
  stars: [
    { x: 0.14, y: 0.34, mag: 3.2 },
    { x: 0.28, y: 0.22, mag: 3.1 },
    { x: 0.42, y: 0.34, mag: 2.8 },
    { x: 0.56, y: 0.28, mag: 3.1 },
    { x: 0.68, y: 0.18, mag: 3.3 },
    { x: 0.54, y: 0.44, mag: 2.5 },
    { x: 0.70, y: 0.46, mag: 3.1 },
    { x: 0.84, y: 0.34, mag: 3.2 },
    { x: 0.72, y: 0.58, mag: 3.1 },
    { x: 0.78, y: 0.70, mag: 3.3 },
    { x: 0.24, y: 0.58, mag: 3.2 },
    { x: 0.32, y: 0.82, mag: 3.3 },
    { x: 0.54, y: 0.78, mag: 3.1 },
    { x: 0.66, y: 0.88, mag: 3.2 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [2, 5], [5, 6], [6, 7], [6, 8], [8, 9], [0, 10], [10, 11], [11, 12], [12, 13]],
};

const CAPRICORN_CONSTELLATION = {
  name: 'Capricorn',
  stars: [
    { x: 0.10, y: 0.34, mag: 3.1 },
    { x: 0.28, y: 0.34, mag: 3.2 },
    { x: 0.48, y: 0.36, mag: 3.3 },
    { x: 0.72, y: 0.28, mag: 3.1 },
    { x: 0.88, y: 0.20, mag: 2.9 },
    { x: 0.82, y: 0.12, mag: 3.3 },
    { x: 0.66, y: 0.78, mag: 3.0 },
    { x: 0.56, y: 0.88, mag: 3.2 },
    { x: 0.38, y: 0.74, mag: 3.3 },
    { x: 0.22, y: 0.56, mag: 3.2 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [4, 6], [6, 7], [7, 8], [8, 9], [9, 0]],
};

const AQUARIUS_CONSTELLATION = {
  name: 'Aquarius',
  stars: [
    { x: 0.18, y: 0.28, mag: 3.0 },
    { x: 0.30, y: 0.14, mag: 3.2 },
    { x: 0.40, y: 0.16, mag: 3.1 },
    { x: 0.50, y: 0.10, mag: 2.6 },
    { x: 0.50, y: 0.34, mag: 3.1 },
    { x: 0.70, y: 0.22, mag: 3.2 },
    { x: 0.90, y: 0.26, mag: 3.1 },
    { x: 0.28, y: 0.44, mag: 3.3 },
    { x: 0.36, y: 0.58, mag: 3.2 },
    { x: 0.30, y: 0.84, mag: 3.4 },
    { x: 0.34, y: 0.70, mag: 3.3 },
    { x: 0.60, y: 0.56, mag: 3.3 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [3, 5], [5, 6], [0, 7], [7, 8], [8, 10], [10, 9], [4, 11]],
};

const PISCES_CONSTELLATION = {
  name: 'Pisces',
  stars: [
    { x: 0.14, y: 0.76, mag: 3.2 },
    { x: 0.28, y: 0.56, mag: 3.1 },
    { x: 0.40, y: 0.34, mag: 3.0 },
    { x: 0.46, y: 0.18, mag: 3.2 },
    { x: 0.38, y: 0.08, mag: 3.3 },
    { x: 0.52, y: 0.74, mag: 3.2 },
    { x: 0.68, y: 0.72, mag: 3.1 },
    { x: 0.86, y: 0.72, mag: 2.9 },
    { x: 0.94, y: 0.72, mag: 3.0 },
    { x: 0.98, y: 0.64, mag: 3.2 },
    { x: 0.94, y: 0.56, mag: 3.2 },
    { x: 0.86, y: 0.56, mag: 3.3 },
  ],
  lines: [[0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 7]],
};

// Transform a normalised constellation definition into pixel coordinates
// centred at (cx, cy) with the given spread, scale, and rotation.
function placeConstellation(def, cx, cy, spread, scale, rotation) {
  const s = spread * scale;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  const stars = def.stars.map((star) => {
    // Centre the 0-1 coords around origin, scale, rotate, then translate
    const nx = (star.x - 0.5) * s;
    const ny = (star.y - 0.5) * s;
    const rx = nx * cos - ny * sin;
    const ry = nx * sin + ny * cos;

    // Map magnitude to visual size & brightness (lower mag = brighter/bigger)
    const magNorm = Math.max(0, Math.min(1, (5 - star.mag) / 4)); // 0-1, 1 = brightest
    const size = 1.2 + magNorm * 2.2; // 1.2 - 3.4px core radius
    const brightness = 0.4 + magNorm * 0.45; // 0.4 - 0.85 opacity

    return {
      x: cx + rx,
      y: cy + ry,
      size,
      brightness,
      twinkleSpeed: 3 + Math.random() * 2.5,
      twinkleDelay: Math.random() * 4,
    };
  });

  const lines = def.lines.map(([from, to]) => ({ from, to }));

  return { stars, lines, name: def.name };
}

// Animated constellations background — real zodiac signs, premium quality
const ConstellationField = () => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 700 });

  // Measure container on mount and resize
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Place Aries constellation — centre-right of the hero, clearly visible
  const constellations = useMemo(() => {
    const w = dimensions.width;
    const h = dimensions.height;
    const minDim = Math.min(w, h);

    // Position Aries in the right portion of the hero, slightly more compact
    // so the silhouette reads closer to the visual reference.
    const cx = w * 0.77;
    const cy = h * 0.4;
    const spread = minDim * 0.34;
    const scale = 1.0;
    const rotation = 0; // no rotation — show the natural arc

    const aries = placeConstellation(ARIES_CONSTELLATION, cx, cy, spread, scale, rotation);

    // Position Taurus on the left side of the hero, keeping it compact and tall
    // so the diagonal-plus-tail silhouette matches the reference more closely.
    const taurus = placeConstellation(TAURUS_CONSTELLATION, w * 0.23, h * 0.42, minDim * 0.26, 1.0, 0);

    const gemini = placeConstellation(GEMINI_CONSTELLATION, w * 0.60, h * 0.18, minDim * 0.20, 1.0, 0);
    const cancer = placeConstellation(CANCER_CONSTELLATION, w * 0.85, h * 0.18, minDim * 0.18, 1.0, 0);
    const leo = placeConstellation(LEO_CONSTELLATION, w * 0.14, h * 0.48, minDim * 0.20, 1.0, 0);
    const virgo = placeConstellation(VIRGO_CONSTELLATION, w * 0.38, h * 0.48, minDim * 0.20, 1.0, 0);
    const libra = placeConstellation(LIBRA_CONSTELLATION, w * 0.63, h * 0.48, minDim * 0.18, 1.0, 0);
    const scorpio = placeConstellation(SCORPIO_CONSTELLATION, w * 0.88, h * 0.48, minDim * 0.20, 1.0, 0);
    const sagittarius = placeConstellation(SAGITTARIUS_CONSTELLATION, w * 0.16, h * 0.78, minDim * 0.22, 1.0, 0);
    const capricorn = placeConstellation(CAPRICORN_CONSTELLATION, w * 0.40, h * 0.78, minDim * 0.20, 1.0, 0);
    const aquarius = placeConstellation(AQUARIUS_CONSTELLATION, w * 0.64, h * 0.78, minDim * 0.18, 1.0, 0);
    const pisces = placeConstellation(PISCES_CONSTELLATION, w * 0.88, h * 0.78, minDim * 0.20, 1.0, 0);

    return [
      { ...aries, delay: 0, id: 0 },
      { ...taurus, delay: 0.35, id: 1 },
      { ...gemini, delay: 0.7, id: 2 },
      { ...cancer, delay: 1.05, id: 3 },
      { ...leo, delay: 1.4, id: 4 },
      { ...virgo, delay: 1.75, id: 5 },
      { ...libra, delay: 2.1, id: 6 },
      { ...scorpio, delay: 2.45, id: 7 },
      { ...sagittarius, delay: 2.8, id: 8 },
      { ...capricorn, delay: 3.15, id: 9 },
      { ...aquarius, delay: 3.5, id: 10 },
      { ...pisces, delay: 3.85, id: 11 },
    ];
  }, [dimensions]);

  // Ambient scattered tiny stars for depth
  const ambientStars = useMemo(() => {
    const w = dimensions.width;
    const h = dimensions.height;
    const count = 35 + Math.floor(Math.random() * 25); // 35-60 ambient dots
    return Array.from({ length: count }, (_, i) => ({
      x: Math.random() * w,
      y: Math.random() * h,
      size: Math.random() * 1.2 + 0.5,
      opacity: Math.random() * 0.25 + 0.1,
      twinkleSpeed: Math.random() * 4 + 4,
      twinkleDelay: Math.random() * 5,
      id: i,
    }));
  }, [dimensions]);

  const svgWidth = dimensions.width;
  const svgHeight = dimensions.height;

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none overflow-hidden">
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Radial gradient for star glow */}
          <radialGradient id="starGlow">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.9" />
            <stop offset="40%" stopColor="#a855f7" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="starGlowBright">
            <stop offset="0%" stopColor="#c084fc" stopOpacity="1" />
            <stop offset="30%" stopColor="#a855f7" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
          </radialGradient>
          {/* Soft blur filter for the glow halos */}
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
          </filter>
          <filter id="tinyGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" />
          </filter>
        </defs>

        {/* Ambient scattered stars — tiny twinkling dots for depth */}
        {ambientStars.map((star) => (
          <motion.circle
            key={`ambient-${star.id}`}
            cx={star.x}
            cy={star.y}
            r={star.size}
            fill="#a855f7"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, star.opacity, star.opacity * 0.4, star.opacity],
            }}
            transition={{
              duration: star.twinkleSpeed,
              delay: star.twinkleDelay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* Real zodiac constellation clusters */}
        {constellations.map((c) => (
          <g key={c.id}>
            {/* Lines: animated draw-on using stroke-dashoffset */}
            {c.lines.map((line, li) => {
              const from = c.stars[line.from];
              const to = c.stars[line.to];
              const dx = to.x - from.x;
              const dy = to.y - from.y;
              const length = Math.sqrt(dx * dx + dy * dy);
              const lineDelay = c.delay + li * 0.22;
              return (
                <motion.line
                  key={`l-${c.id}-${li}`}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="#a855f7"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeDasharray={length}
                  initial={{ strokeDashoffset: length, opacity: 0 }}
                  animate={{ strokeDashoffset: 0, opacity: 0.4 }}
                  transition={{
                    strokeDashoffset: {
                      duration: 2.0,
                      delay: lineDelay,
                      ease: 'easeInOut',
                    },
                    opacity: {
                      duration: 0.8,
                      delay: lineDelay,
                    },
                  }}
                />
              );
            })}

            {/* Stars: layered circles for glow effect */}
            {c.stars.map((star, si) => {
              const starDelay = c.delay + si * 0.1;
              return (
                <g key={`s-${c.id}-${si}`}>
                  {/* Outer glow halo (blurred, large) */}
                  <motion.circle
                    cx={star.x}
                    cy={star.y}
                    r={star.size * 5}
                    fill="url(#starGlow)"
                    filter="url(#softGlow)"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                      opacity: [0, star.brightness * 0.45, star.brightness * 0.2, star.brightness * 0.45],
                      scale: [0, 1.1, 1],
                    }}
                    transition={{
                      opacity: {
                        duration: star.twinkleSpeed,
                        delay: starDelay + 0.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      },
                      scale: {
                        duration: 0.8,
                        delay: starDelay,
                        ease: 'easeOut',
                      },
                    }}
                  />
                  {/* Mid glow ring */}
                  <motion.circle
                    cx={star.x}
                    cy={star.y}
                    r={star.size * 2.5}
                    fill="url(#starGlowBright)"
                    filter="url(#tinyGlow)"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                      opacity: [0, star.brightness * 0.65, star.brightness * 0.35, star.brightness * 0.65],
                      scale: [0, 1.15, 1],
                    }}
                    transition={{
                      opacity: {
                        duration: star.twinkleSpeed,
                        delay: starDelay + 0.3,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      },
                      scale: {
                        duration: 0.6,
                        delay: starDelay,
                        ease: 'easeOut',
                      },
                    }}
                  />
                  {/* Core star (solid, bright, small) */}
                  <motion.circle
                    cx={star.x}
                    cy={star.y}
                    r={star.size}
                    fill="#c084fc"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                      opacity: [0, star.brightness, star.brightness * 0.6, star.brightness],
                      scale: [0, 1.3, 1],
                    }}
                    transition={{
                      opacity: {
                        duration: star.twinkleSpeed,
                        delay: starDelay,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      },
                      scale: {
                        duration: 0.5,
                        delay: starDelay,
                        ease: [0.34, 1.56, 0.64, 1],
                      },
                    }}
                  />
                </g>
              );
            })}
          </g>
        ))}
      </svg>
    </div>
  );
};

const AstrologyLandingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Add loading state
  const { products, loading: productsLoading, error: productsError, retry: retryProducts } = useProducts();

  const valuePropositions = [
    {
      icon: "Star",
      title: "Mapa Astral Completo",
      text: "Analise detalhada de todas as posicoes planetarias, casas astrologicas e aspectos do seu mapa natal."
    },
    {
      icon: "MessageCircle",
      title: "Chat com IA Astrologa",
      text: "Converse sobre seu mapa astral com uma IA especializada que conhece cada detalhe do seu mapa."
    },
    {
      icon: "Sparkles",
      title: "Analise Personalizada",
      text: "Interpretacoes unicas baseadas nas posicoes reais dos astros no momento do seu nascimento."
    },
  ];

  const faqItems = [
    {
      question: "Como é feito o mapa astral?",
      answer: "Utilizamos dados precisos do seu nascimento e tecnologia avançada para calcular as posições exatas dos astros no momento do seu nascimento."
    },
    {
      question: "Quanto tempo leva para receber meu mapa?",
      answer: "O mapa é gerado instantaneamente após a confirmação do pagamento e preenchimento dos dados."
    },
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/profile'); // Redireciona para a página de perfil se o usuário estiver autenticado
      }
      setLoading(false); // Set loading to false after auth check
    });

    return () => unsubscribe();
  }, [navigate]);

  if (loading) {
    return null; // Prevent rendering the page while checking auth
  }

  // Helper function to format price
  const formatPrice = (priceInCents) => {
    const price = priceInCents / 100;
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const handlePlanSelection = async (product) => {
    const planData = {
      priceId: product.prices[0]?.id,
      planName: product.name,
      price: product.prices[0]?.unit_amount
    };
    
    // Redirect to signup with plan info
    navigate(`/signup?plan=${encodeURIComponent(JSON.stringify(planData))}`);
  };

  const features = [
    {
      icon: "🌟",
      title: "Mapa Natal Completo",
      description: "Análise detalhada dos planetas no momento do seu nascimento"
    },
    {
      icon: "🌙",
      title: "Previsões Personalizadas",
      description: "Descubra as influências astrológicas em sua vida atual"
    },
    {
      icon: "❤️",
      title: "Sinastria Amorosa",
      description: "Compare mapas astrais e descubra compatibilidades"
    }
  ];

  // Add new helper function for feature availability
  const getFeatureAvailability = (feature, productTier) => {
    switch (productTier) {
      case 'basic':
        return feature.basicAvailable;
      case 'premium':
        return feature.premiumAvailable;
      case 'vip':
        return feature.vipAvailable;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      {/* Navigation */}
      <PublicNavbar /> {/* Utiliza o novo componente PublicNavbar */}

      {/* Header Section */}
      <header className="pt-32 pb-24 px-4 text-center relative overflow-hidden">
        {/* Animated constellations */}
        <ConstellationField />

        {/* Ambient glow orbs */}
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-gradient-to-br from-purple-300/20 to-pink-300/20 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-gradient-to-br from-pink-300/15 to-purple-300/15 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="relative max-w-4xl mx-auto space-y-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-serif text-5xl md:text-7xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight"
          >
            Descubra Seu<br className="hidden md:block" /> Mapa Astral
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed"
          >
            Entenda sua jornada através das estrelas com análises personalizadas e profundas do seu mapa astral
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button to="/signup" size="lg">
              Criar Meu Mapa Grátis
            </Button>
          </motion.div>
        </div>
      </header>

      {/* Transition: Hero → Features */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent"></div>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-12">
            Nossos Serviços
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-xl transition-shadow"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transition: Features → How It Works */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent"></div>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-purple-50/80 to-pink-50/60">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Como Funciona
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            { [
              { step: "1", title: "Cadastre-se", description: "Crie sua conta em poucos segundos" },
              { step: "2", title: "Escolha seu plano", description: "Selecione o plano ideal para você" },
              { step: "3", title: "Informe seus dados", description: "Digite data, hora e local de nascimento" },
              { step: "4", title: "Receba seu mapa", description: "Acesse análises detalhadas instantaneamente" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-purple-600 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transition: How It Works → Value Props */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent"></div>

      {/* Value Propositions Section */}
      <section className="py-20 bg-white/60 backdrop-blur-sm">
        <h3 className="font-serif text-3xl md:text-4xl font-bold text-center mb-12">
          <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Por que escolher o MapaAstral.ai?
          </span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
          {valuePropositions.map((prop, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-purple-100 text-center"
            >
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-purple-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-3">{prop.title}</h4>
              <p className="text-gray-600">{prop.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Transition: Value Props → Pricing */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent"></div>

      {/* Pricing Section */}
      <section id="precos" className="py-20 px-4 bg-gradient-to-b from-purple-50/60 to-white/80">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            Nossos Planos
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Escolha o plano ideal para sua jornada astrológica. Cancele a qualquer momento.
          </p>
          
          {productsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-purple-100 rounded-2xl h-96 animate-pulse" />
              ))}
            </div>
          ) : productsError ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Nao foi possivel carregar os planos.</p>
              <Button onClick={retryProducts} size="sm">
                Tentar novamente
              </Button>
            </div>
          ) : (
            <PricingTable products={products} onPlanSelect={handlePlanSelection} />
          )}
        </div>
      </section>

      {/* Transition: Pricing → FAQ */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent"></div>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white/60 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Perguntas Frequentes
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow group">
                <summary className="font-semibold cursor-pointer text-gray-800 hover:text-purple-600 transition-colors">
                  {item.question}
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-purple-950 to-[#0f0a1e] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            {/* Brand — wider column */}
            <div className="md:col-span-5">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                  <Star className="w-4.5 h-4.5 text-white" fill="white" />
                </div>
                <span className="text-xl font-bold tracking-tight">
                  MapaAstral<span className="text-pink-400">.ai</span>
                </span>
              </div>
              <p className="text-purple-300/80 text-sm leading-relaxed max-w-sm mb-6">
                Descubra o que as estrelas revelam sobre você. Análises astrológicas personalizadas com inteligência artificial.
              </p>
              <Button to="/signup" size="sm">
                <Sparkles className="w-4 h-4" />
                Criar Meu Mapa Grátis
              </Button>
            </div>

            {/* Legal links */}
            <div className="md:col-span-3">
              <h4 className="font-semibold text-white/60 mb-4 text-xs uppercase tracking-widest">Legal</h4>
              <ul className="space-y-3">
                <li><a href="/privacy-policy" className="text-purple-300/70 hover:text-pink-300 transition-colors text-sm">Política de Privacidade</a></li>
                <li><a href="/terms-of-use" className="text-purple-300/70 hover:text-pink-300 transition-colors text-sm">Termos de Uso</a></li>
                <li><a href="/cookie-policy" className="text-purple-300/70 hover:text-pink-300 transition-colors text-sm">Política de Cookies</a></li>
                <li><a href="/lgpd" className="text-purple-300/70 hover:text-pink-300 transition-colors text-sm">Seus Direitos (LGPD)</a></li>
              </ul>
            </div>

            {/* Contact + Support */}
            <div className="md:col-span-4">
              <h4 className="font-semibold text-white/60 mb-4 text-xs uppercase tracking-widest">Contato</h4>
              <ul className="space-y-3">
                <li>
                  <a href="mailto:contato@mapaastral.ai" className="inline-flex items-center gap-2 text-purple-300/70 hover:text-pink-300 transition-colors text-sm">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    contato@mapaastral.ai
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Divider + Copyright */}
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-xs">&copy; {new Date().getFullYear()} MapaAstral.ai — Todos os direitos reservados.</p>
            <p className="text-white/20 text-xs">Feito com ✦ no Brasil</p>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
};

export default AstrologyLandingPage;
